// «Consumo de datos»: cuántos bytes ha servido el servidor por /files (fotos, videos
// y documentos) en los últimos 7 o 30 días — total, por tipo, por día y los archivos
// más servidos. Sirve para decidir CON DATOS dónde está el gasto de transferencia.
//
// Es una sección plegable y cerrada por defecto: no pide nada al servidor hasta que
// el administrador la abre. Solo mide lo servido por /files: no la API ni las subidas
// (la aclaración se muestra siempre que la sección está abierta).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Segmented, Skeleton, Tooltip, Typography } from "antd";
import { BarChartOutlined, DownOutlined, ReloadOutlined, UpOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { useFiltroPersistido } from "../../../hooks/useFiltroPersistido";
import {
  almacenamientoApi,
  type ConsumoAlmacenamiento,
  type DiasConsumo,
  type TipoConsumo,
} from "../../../services/almacenamiento.api";
import {
  ETIQUETA_TIPO_CONSUMO,
  ORDEN_TIPOS_CONSUMO,
  barrasPorDia,
  formatDiaCorto,
  formatDiaDMA,
  formatPeso,
  formatPeticiones,
  formatPorcentaje,
  nombreConsumo,
  porcentaje,
  topVisible,
} from "../almacenamientoUtils";

const { Text } = Typography;

/** Aclaración de alcance por si el servidor no la manda. */
export const NOTA_ALCANCE_UI =
  "Solo mide lo servido por /files (fotos, videos y documentos). No incluye la API ni las subidas.";

const OPCIONES_DIAS: Array<{ value: DiasConsumo; label: string }> = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
];

const CLAVE_ABIERTO = "almacenamiento.consumoAbierto";
const CLAVE_DIAS = "almacenamiento.consumoDias";

const esDias = (v: unknown): boolean => v === 7 || v === 30;

/** (i) de la tarjeta: qué mide el consumo, qué no y cómo leerlo. */
const AyudaConsumo: React.FC = () => (
  <InfoAyuda
    titulo="Cómo se mide el gasto de datos"
    testId="ayuda-consumo"
    className="alm-ayuda-chica"
  >
    <AyudaBloque titulo="Qué mide">
      Los datos que el servidor entregó por <b>/files</b> (fotos, videos y documentos) en los
      últimos 7 o 30 días: el total, el reparto por tipo, el consumo de cada día (hora de Chile) y
      los archivos más servidos. Cuenta lo que realmente se envió, incluso si la persona cortó el
      video a la mitad.
    </AyudaBloque>
    <AyudaBloque titulo="Los cinco tipos">
      Fotos, miniaturas, videos, documentos y <b>Video (versión liviana)</b>. La versión liviana
      se cuenta aparte del video original para ver cuánto de lo que se ve ya es la copia barata:
      si «Videos» pesa mucho y «Video (versión liviana)» poco, la gente está viendo en HD o hay
      videos sin versión liviana (se generan con el botón «Generar versiones livianas» de más
      arriba).
    </AyudaBloque>
    <AyudaBloque titulo="Qué NO mide">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>La API del sistema (listas, formularios) ni las subidas de archivos.</li>
        <li>
          Lo que el navegador reutiliza de lo que ya descargó: si lo revalida, cuenta una petición
          con 0 bytes («sin cambios»); si ni siquiera pregunta, no aparece.
        </li>
        <li>Lo anterior a la fecha en que se activó esta medición.</li>
      </ul>
    </AyudaBloque>
    <AyudaBloque titulo="Cómo se actualiza">
      El servidor suma lo que entrega y lo guarda en la base cada minuto; al abrir la tarjeta o
      pulsar actualizar vuelca primero lo pendiente, así que lo que ves está al día. Se conservan
      120 días; aquí se ven 7 o 30.
    </AyudaBloque>
  </InfoAyuda>
);

const ConsumoDatos: React.FC = () => {
  const [abierto, setAbierto] = useFiltroPersistido<boolean>(CLAVE_ABIERTO, false, {
    validar: (v) => typeof v === "boolean",
  });
  const [dias, setDias] = useFiltroPersistido<DiasConsumo>(CLAVE_DIAS, 7, { validar: esDias });
  const [datos, setDatos] = useState<ConsumoAlmacenamiento | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);
  /** Número del último pedido: una respuesta vieja (otro período) se descarta. */
  const pedido = useRef(0);

  const cargar = useCallback(async (d: DiasConsumo) => {
    const mio = ++pedido.current;
    setCargando(true);
    setError(false);
    const r = await almacenamientoApi.consumo(d);
    if (mio !== pedido.current) return;
    setCargando(false);
    if (!r?.data) {
      setError(true);
      return;
    }
    setDatos(r.data);
  }, []);

  useEffect(() => {
    if (abierto) void cargar(dias);
  }, [abierto, dias, cargar]);

  // Solo se muestra lo del período elegido: los números de otro período con el
  // rótulo nuevo serían un dato falso.
  const vigente = datos && datos.dias === dias ? datos : null;

  return (
    <section className="alm-consumo" aria-label="Consumo de datos">
      <div className="alm-consumo-cab">
        <div className="alm-consumo-titulo">
          <Button
            type="text"
            className="alm-btn-tactil alm-consumo-toggle"
            aria-expanded={abierto}
            aria-controls="alm-consumo-cuerpo"
            icon={<BarChartOutlined />}
            onClick={() => setAbierto((v) => !v)}
          >
            <Text strong>Consumo de datos</Text>
            {abierto ? <UpOutlined /> : <DownOutlined />}
          </Button>
          {/* Fuera del botón de plegar: un botón dentro de otro no es HTML válido. */}
          <AyudaConsumo />
        </div>
        {abierto ? (
          <div className="alm-consumo-acciones">
            <Segmented<DiasConsumo>
              value={dias}
              options={OPCIONES_DIAS}
              onChange={(v) => setDias(v)}
              aria-label="Período del consumo"
            />
            <Button
              className="alm-btn-tactil"
              icon={<ReloadOutlined />}
              loading={cargando && !!vigente}
              onClick={() => void cargar(dias)}
              aria-label="Actualizar el consumo"
            />
          </div>
        ) : (
          <Text type="secondary" className="alm-consumo-pista">
            Cuánto se descarga de fotos, videos y documentos
          </Text>
        )}
      </div>

      {abierto && (
        <div id="alm-consumo-cuerpo" className="alm-consumo-cuerpo">
          <Text type="secondary" className="alm-consumo-alcance">
            {vigente?.nota || NOTA_ALCANCE_UI}
          </Text>

          {error && !vigente ? (
            <Alert
              type="error"
              showIcon
              message="No se pudo leer el consumo de datos"
              description="Falló la consulta. No se muestran números para no mostrar datos falsos."
              action={
                <Button
                  className="alm-btn-tactil"
                  loading={cargando}
                  onClick={() => void cargar(dias)}
                >
                  Reintentar
                </Button>
              }
            />
          ) : !vigente ? (
            <div aria-busy="true" aria-label="Cargando el consumo">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : (
            <>
              {error && (
                <Alert
                  type="warning"
                  showIcon
                  className="alm-consumo-aviso"
                  message="No se pudo actualizar: estos números pueden estar desactualizados."
                  action={
                    <Button onClick={() => void cargar(dias)} loading={cargando}>
                      Reintentar
                    </Button>
                  }
                />
              )}
              <CuerpoConsumo datos={vigente} />
            </>
          )}
        </div>
      )}
    </section>
  );
};

const COLOR_TIPO: Record<TipoConsumo, string> = {
  video: "alm-consumo-color-video",
  image: "alm-consumo-color-image",
  doc: "alm-consumo-color-doc",
  thumb: "alm-consumo-color-thumb",
  preview: "alm-consumo-color-preview",
};

const CuerpoConsumo: React.FC<{ datos: ConsumoAlmacenamiento }> = ({ datos }) => {
  if (datos.totalPeticiones === 0 && datos.totalBytes === 0) {
    return (
      <div className="alm-consumo-vacio" role="status">
        <Text strong>Todavía no hay consumo registrado en este período.</Text>
        <Text type="secondary">
          La medición empezó cuando se activó esta función y se actualiza cada minuto. Vuelve a
          mirar más tarde.
        </Text>
      </div>
    );
  }

  const tipos = ORDEN_TIPOS_CONSUMO.map((t) => {
    const f = datos.porTipo.find((x) => x.tipo === t);
    return { tipo: t, bytes: f?.bytes ?? 0, peticiones: f?.peticiones ?? 0 };
  });
  const barras = barrasPorDia(datos.porDia);
  const mayor = barras.reduce((m, b) => (b.bytes > m.bytes ? b : m), barras[0]);
  const top = topVisible(datos.top);
  const paso = datos.dias === 30 ? 5 : 1;

  return (
    <>
      <div className="alm-tarjetitas alm-consumo-totales">
        <div className="alm-tarjetita">
          <Text type="secondary">Entregado en {datos.dias} días</Text>
          <Text strong className="alm-tarjetita-valor">
            {formatPeso(datos.totalBytes)}
          </Text>
          <Text type="secondary">
            del {formatDiaDMA(datos.desde)} al {formatDiaDMA(datos.hasta)}
          </Text>
        </div>
        <div className="alm-tarjetita">
          <Text type="secondary">Peticiones</Text>
          <Text strong className="alm-tarjetita-valor">
            {datos.totalPeticiones.toLocaleString("es-CL")}
          </Text>
          <Text type="secondary">incluye las que el navegador resolvió con «sin cambios»</Text>
        </div>
      </div>

      <div className="alm-desgloses">
        <div className="alm-desglose">
          <Text strong>Por tipo</Text>
          <ul className="alm-consumo-tipos">
            {tipos.map((t) => {
              const pct = porcentaje(t.bytes, datos.totalBytes);
              return (
                <li key={t.tipo} className="alm-consumo-tipo">
                  <span className="alm-consumo-tipo-nombre">
                    {ETIQUETA_TIPO_CONSUMO[t.tipo]}
                    <Text type="secondary"> · {formatPeticiones(t.peticiones)}</Text>
                  </span>
                  <span className="alm-barra alm-consumo-tipo-barra" aria-hidden="true">
                    <span
                      className={`alm-barra-relleno ${COLOR_TIPO[t.tipo]}`}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="alm-consumo-tipo-valor">
                    {formatPeso(t.bytes)} <Text type="secondary">({formatPorcentaje(pct)})</Text>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="alm-desglose">
          <Text strong>Por día</Text>
          <div
            className="alm-consumo-serie"
            role="img"
            aria-label={
              mayor && mayor.bytes > 0
                ? `Entregado por día, de ${formatDiaDMA(datos.desde)} a ${formatDiaDMA(datos.hasta)}. El día de más consumo fue el ${formatDiaDMA(mayor.fecha)} con ${formatPeso(mayor.bytes)}.`
                : "Entregado por día: sin consumo"
            }
          >
            {barras.map((b, i) => (
              <Tooltip key={b.fecha} title={`${formatDiaDMA(b.fecha)}: ${formatPeso(b.bytes)}`}>
                <div className="alm-consumo-dia">
                  <span className="alm-consumo-dia-hueco">
                    <span className="alm-consumo-dia-barra" style={{ height: `${b.pct}%` }} />
                  </span>
                  <span className="alm-consumo-dia-etiqueta">
                    {(barras.length - 1 - i) % paso === 0 ? formatDiaCorto(b.fecha) : ""}
                  </span>
                </div>
              </Tooltip>
            ))}
          </div>
          <Text type="secondary" className="alm-consumo-serie-pie">
            Cada barra es un día (hora de Chile). Más alta = más datos entregados.
          </Text>
        </div>
      </div>

      <div className="alm-consumo-top-bloque">
        <Text strong>
          {top.length === 1
            ? "El archivo más servido"
            : top.length === 0
              ? "Los archivos más servidos"
              : `Los ${top.length} archivos más servidos`}
        </Text>
        {top.length === 0 ? (
          <Text type="secondary" className="alm-consumo-top-vacio">
            Ningún archivo en este período.
          </Text>
        ) : (
          <ol className="alm-consumo-top">
            {top.map((a, i) => {
              const nombre = nombreConsumo(a);
              return (
                <li key={`${a.tipo}:${a.clave}`} className="alm-consumo-top-fila">
                  <span className="alm-consumo-top-n">{i + 1}</span>
                  <span className="alm-consumo-top-quien">
                    <Tooltip title={a.clave}>
                      <span className="alm-consumo-top-nombre">{nombre}</span>
                    </Tooltip>
                    <Text type="secondary" className="alm-consumo-top-meta">
                      {[a.modulo, ETIQUETA_TIPO_CONSUMO[a.tipo] ?? a.tipo]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </span>
                  <span className="alm-consumo-top-cifras">
                    <Text strong>{formatPeso(a.bytes)}</Text>
                    <Text type="secondary">{formatPeticiones(a.peticiones)}</Text>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </>
  );
};

export default ConsumoDatos;
